import {
  IHtmlHighlight,
  IPdfAnnotations,
  IPdfHighlight,
  PopoverConfiguration,
  PopoverConfigurationOptions,
  PopoverOptionComments,
  PopoverOptionNotes,
} from "../../../typings/annotate/annotation";
import AnnotatePopover, {
  PopoverInternal,
} from "../annotate-popover/annotate-popover";
import { FloatingToolbar, zoomLevels } from "./floating-toolbar";
import { PdfViewer } from "./pdf-viewer";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CommentContext } from "../hooks/comment-context";

export function PDFAnnotator(props: {
  fileUrl: string;
  id: string;
  annotations: IPdfAnnotations;
  popoverConfiguration: PopoverConfiguration;
}) {
  const [showComments, setShowComments] = useState<boolean>(false);
  const [generation, setGeneration] = useState<number>(0);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [selectedThread, setSelectedThread] = useState<string | undefined>();

  // Memoize highlights state to prevent unnecessary re-renders
  const [highlights, setHighlights] = useState<IPdfHighlight[]>(
    () => props.annotations.highlights,
  );

  // Memoize the selected highlight
  const selectedHighlight = useMemo(
    () => highlights.find((x) => x.id === selectedThread),
    [highlights, selectedThread],
  );

  // Use useCallback for handlers to prevent recreation on each render
  const handleSetFakeHighlight = useCallback(
    (h: IPdfHighlight) => {
      if (props.popoverConfiguration.focusMode) {
        setHighlights([]);
        return;
      }
      setHighlights((prev) => [...prev.filter((x) => x.id !== "temp"), h]);
      setSelectedThread("temp");
    },
    [props.popoverConfiguration.focusMode],
  );

  const handleSelectThread = useCallback((thread: string) => {
    setShowComments(false);
    setSelectedThread(thread);
  }, []);

  // Optimize highlights update
  useEffect(() => {
    if (selectedThread === "temp") {
      setHighlights((currentHighlights) => {
        const tempHighlight = currentHighlights.find((x) => x.id === "temp");
        if (!tempHighlight) return props.annotations.highlights;

        return [...props.annotations.highlights, tempHighlight];
      });
    } else {
      setHighlights(props.annotations.highlights);
    }
  }, [props.annotations, selectedThread]);

  // Clean up temp highlights
  useEffect(() => {
    if (selectedThread != "temp" && highlights.some((x) => x.id === "temp")) {
      // If a highlight was selected, and it's not temp, then remove any temp highlight from the list
      setHighlights((highlights) => highlights.filter((x) => x.id !== "temp"));
    }
  }, [selectedThread]);

  // Handle focus mode
  useEffect(() => {
    if (props.popoverConfiguration.focusMode) {
      setHighlights([]);
    }
  }, [props.popoverConfiguration.focusMode]);

  const contentRef = useRef<HTMLDivElement>(null);

  // Memoize comment context value
  const commentContextValue = useMemo(
    () => ({
      showComments: (h: IHtmlHighlight) => {
        const option = props.popoverConfiguration.options.find(
          (x) => x.type === PopoverConfigurationOptions.Comments,
        ) as PopoverOptionComments;
        option?.commentsClicked?.(h);
      },
      showNotes: (h: IHtmlHighlight) => {
        const option = props.popoverConfiguration.options.find(
          (x) => x.type === PopoverConfigurationOptions.Notes,
        ) as PopoverOptionNotes;
        option?.noteClicked?.(h);
      },
      selectThread: handleSelectThread,
      contentRef: contentRef.current,
      selectedThread: selectedHighlight?.id,
    }),
    [props.popoverConfiguration, handleSelectThread, selectedHighlight?.id],
  );

  return (
    <div className="w-full relative">
      <div className="w-full overflow-y-auto">
        <CommentContext.Provider value={commentContextValue}>
          <div
            className="w-full relative"
            ref={contentRef}
            style={{ contain: "paint layout" }} // Add containment to reduce reflow
          >
            <PdfViewer
              {...props}
              zoomLevel={zoomLevel}
              pdfRenderIncrement={() => {
                setGeneration((g) => g + 1);
              }}
              annotations={{
                highlights,
                comments: props.annotations.comments,
              }}
              setFakeHighlight={handleSetFakeHighlight}
            />
          </div>

          {selectedHighlight && (
            <AnnotatePopover
              generatedHtml={generation}
              closePopover={() => setSelectedThread("")}
            >
              <PopoverInternal
                popoverConfiguration={props.popoverConfiguration}
                highlight={selectedHighlight}
                closePopover={() => setSelectedThread("")}
                commentClicked={(hId: string) => {
                  setSelectedThread(hId);
                  setShowComments(true);
                }}
              />
            </AnnotatePopover>
          )}
        </CommentContext.Provider>
      </div>
      <FloatingToolbar setZoomLevel={setZoomLevel} zoomLevel={zoomLevel} />
    </div>
  );
}
